import React, { Fragment, useState } from 'react';
import { Button, Modal, ModalBody, ModalHeader, ModalFooter, Form, Label, Collapse, Badge } from 'reactstrap';
import { omitBy, pick, isUndefined, mapValues, difference, flatMap } from 'lodash';
import classnames from 'classnames';
import { useToggle } from 'react-use';
import { toast } from 'react-toastify';
import dedent from 'dedent';

import firebase from '../../firebase';
import { checkPlaceUniqueness } from '../../util';
import useFormState from '../hooks/useFormState';
import Field from '../Field';
import PlaceForm from '../forms/PlaceForm';
import { fields, fieldGroups } from '../../shared/models/event';

const db = firebase.firestore();
const placesRef = db.collection('places');
const reminderMailBodyDescription = dedent`
イベント名: {{EVENT_NAME}}
開催日: {{LECTURE_DATE}}
商品種別: {{LECTURE_PRODUCT_TYPE}}
レクチャー種別: {{LECTURE_TYPE}}
対象レベル: {{LECTURE_LEVEL}}
対象年齢: {{LECTURE_AGE}}
価格: {{LECTURE_PRICE}}
`;

export default function EventFormModal(props) {
  const { places, staffs, user, isOpen, values, onClickClose } = props;
  const [showsNewPlaceForm, toggleNewPlaceForm] = useToggle();
  const [openGroups, setOpenGroups] = useState({ 0: true });
  const isNew = !values;
  const statedFields = useFormState(values, fields({ places, staffs, user }), isOpen);
  const isUnsubmittable = Object.values(statedFields).some(_ => !_.isValid);
  const onSubmit = (event) => {
    event.preventDefault();
    if(isUnsubmittable) return;
    props.onSubmit({ ...mapValues(statedFields, 'value'), });
  };
  const onSubmitNewPlaceForm = async (values) => {
    try {
      const isUnique = await checkPlaceUniqueness(values);
      if(!isUnique) return toast.error('その開催場所名は該当都道府県にすでに存在します');
      const { id } = await placesRef.add({ ...values, tenantId: user.tenantId, createdAt: new Date(), addedBy: omitBy(pick(user, ['uid', 'email', 'displayName']), isUndefined), });
      toast.success('開催場所を追加しました');
      statedFields.placeId.setValue(id);
    } catch(e) {
      toast.error('失敗しました');
      console.error(e);
    }
    toggleNewPlaceForm();
  };

  const renderField = (fieldName) => {
    const fieldSetting = statedFields[fieldName];
    if(!fieldSetting) return null;

    if(fieldName === 'reminderMailBody') {
      return (
        <div key={fieldName}>
          <Label>{fieldSetting.label}</Label>
          <div className="alert alert-info small" style={{ whiteSpace: 'pre-line' }}>
            {reminderMailBodyDescription}
          </div>
          <Field
            documentName="event"
            name={fieldName}
            {...fieldSetting}
            label={null}
          />
        </div>
      );
    }

    return (
      <Fragment key={fieldName}>
        {
          fieldName === 'placeId' && (
            <div>
              <Label>開催場所</Label>
              <Button size="sm" className="ml-2 py-0 px-2" onClick={toggleNewPlaceForm}>
                <span className={classnames('fas', { 'fa-plus': !showsNewPlaceForm, 'fa-times': showsNewPlaceForm })} />
              </Button>
              {
                showsNewPlaceForm && (
                  <div className="card p-3 mb-2">
                    <PlaceForm onSubmit={onSubmitNewPlaceForm} />
                  </div>
                )
              }
            </div>
          )
        }
        <Field
          name={fieldName}
          documentName="event"
          {...fieldSetting}
          {...(fieldName === 'placeId' && { label: null })}
        />
      </Fragment>
    );
  };

  // fieldGroups に未割当のフィールド（将来追加された定義の漏れ防止）
  const groupedFieldNames = flatMap(fieldGroups, 'fields');
  const ungroupedFieldNames = difference(Object.keys(statedFields), groupedFieldNames);
  const groups = [
    ...fieldGroups,
    ...(ungroupedFieldNames.length > 0 ? [{ label: 'その他', fields: ungroupedFieldNames }] : []),
  ];

  return (
    <Modal isOpen={isOpen} style={{ width: '95vw', maxWidth: '95vw', }}>
      <ModalHeader>
        イベント{isNew ? '追加' : '編集'}
      </ModalHeader>
      <Form onSubmit={onSubmit}>
        <ModalBody>
          {
            groups.map((group, i) => {
              const isGroupOpen = !!openGroups[i];
              const errorCount = group.fields
                .map(_ => statedFields[_])
                .filter(Boolean)
                .reduce((x, _) => x + _.validationErrors.length, 0);
              return (
                <div className="card mb-2" key={group.label}>
                  <div
                    className="card-header d-flex align-items-center justify-content-between"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setOpenGroups({ ...openGroups, [i]: !isGroupOpen })}
                  >
                    <span>
                      {group.label}
                      {errorCount > 0 && <Badge color="danger" className="ml-2">{errorCount}</Badge>}
                    </span>
                    <span className={classnames('fas', { 'fa-chevron-down': isGroupOpen, 'fa-chevron-right': !isGroupOpen })} />
                  </div>
                  <Collapse isOpen={isGroupOpen}>
                    <div className="card-body">
                      {group.fields.map(renderField)}
                    </div>
                  </Collapse>
                </div>
              );
            })
          }
        </ModalBody>
        <ModalFooter>
          <Button className="cancel" color="secondary" onClick={onClickClose}>閉じる</Button>
          <Button className="save" type="submit" color="primary" onClick={onSubmit} disabled={isUnsubmittable}>保存</Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};
